import { useCallback, useEffect, useState } from "react";
import {
  MessageSquareText,
  Megaphone,
  PencilLine,
  RadioTower,
  RefreshCcw,
  Search,
  Send,
  Settings2,
  Trash2,
} from "lucide-react";
import API from "../../services/api";
import CustomSelect from "../../components/CustomSelect";
import PageModal from "../../components/PageModal";
import { useToast } from "../../components/Toast";
import { formatTimestamp } from "./adminShared";
import "./AdminShared.css";
import "./AdminBroadcast.css";

const BROADCAST_TYPE_OPTIONS = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "urgent", label: "Urgent" },
];

const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

const BROADCAST_FILTER_OPTIONS = [
  { value: "all", label: "All Categories" },
  ...BROADCAST_TYPE_OPTIONS,
];

const BROADCAST_TYPE_META = {
  info: { label: "Info", badgeClass: "admin-badge-info" },
  warning: { label: "Warning", badgeClass: "admin-badge-warning" },
  urgent: { label: "Urgent", badgeClass: "admin-badge-danger" },
};

function getErrorMessage(err, fallback = "Request failed.") {
  return err.response?.data?.message || err.response?.data?.msg || err.message || fallback;
}

function getBroadcastTypeMeta(type) {
  return BROADCAST_TYPE_META[type] || BROADCAST_TYPE_META.info;
}

export default function AdminBroadcast() {
  const toast = useToast();
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("broadcast");
  const [broadcastType, setBroadcastType] = useState("info");
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyRefreshing, setHistoryRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState("");
  const [historyCategory, setHistoryCategory] = useState("all");
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [editDraft, setEditDraft] = useState({ message: "", type: "info" });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const showBroadcastPreview = useCallback(({ type, message }) => {
    if (typeof window === "undefined") return;

    window.dispatchEvent(
      new CustomEvent("broadcast-toast-preview", {
        detail: {
          id: `preview-${type}-${Date.now()}`,
          type,
          message,
        },
      }),
    );
  }, []);

  const handlePreviewBroadcast = useCallback(() => {
    if (deliveryMode !== "broadcast") return;
    if (!broadcastMessage.trim()) {
      toast.error("Broadcast message is required for preview.");
      return;
    }

    showBroadcastPreview({
      type: broadcastType,
      message: broadcastMessage.trim(),
    });
  }, [broadcastMessage, broadcastType, deliveryMode, showBroadcastPreview, toast]);

  const loadTelegramStatus = useCallback(async () => {
    try {
      const res = await API.get("/admin/telegram/status");
      setTelegramStatus(res.data || null);
    } catch {
      setTelegramStatus(null);
    }
  }, []);

  const loadBroadcasts = useCallback(
    async ({ limit, offset, initial = false }) => {
      if (initial) {
        setHistoryLoading(true);
      } else {
        setHistoryRefreshing(true);
      }

      setHistoryError("");

      try {
        const res = await API.get("/admin/broadcasts", {
          params: { limit, offset },
        });
        const payload = res.data || {};

        setBroadcasts(Array.isArray(payload.data) ? payload.data : []);
        setHistoryTotal(Number(payload.total) || 0);
        setHistoryLimit(Number(payload.limit) || limit);
        setHistoryOffset(Number(payload.offset) || offset);
      } catch (err) {
        const message = getErrorMessage(err, "Failed to load broadcasts.");
        setHistoryError(message);
        if (!initial) {
          toast.error(message);
        }
      } finally {
        setHistoryLoading(false);
        setHistoryRefreshing(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadBroadcasts({ limit: 10, offset: 0, initial: true });
    loadTelegramStatus();
  }, [loadBroadcasts, loadTelegramStatus]);

  const handleBroadcastSubmit = async (event) => {
    event.preventDefault();
    if (!broadcastMessage.trim()) {
      toast.error("Broadcast message is required.");
      return;
    }

    setActionBusy(deliveryMode);
    try {
      if (deliveryMode === "telegram") {
        const response = await API.post("/admin/telegram/broadcast", {
          message: broadcastMessage.trim(),
        });
        const payload = response.data || {};
        const notice = payload.message || "Telegram broadcast sent.";
        if (payload.failedRecipients > 0) {
          toast.warning(notice);
        } else {
          toast.success(notice);
        }
        await loadTelegramStatus();
      } else {
        const response = await API.post("/admin/broadcast", {
          message: broadcastMessage.trim(),
          type: broadcastType,
        });
        toast.success(response.data?.msg || "Broadcast sent successfully.");
        await loadBroadcasts({ limit: historyLimit, offset: 0 });
      }
      setBroadcastMessage("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionBusy(null);
    }
  };

  const openEditModal = (broadcast) => {
    setEditingBroadcast(broadcast);
    setEditDraft({
      message: broadcast.message || "",
      type: broadcast.type || "info",
    });
  };

  const closeEditModal = () => {
    if (editSubmitting) return;
    setEditingBroadcast(null);
    setEditDraft({ message: "", type: "info" });
  };

  const handleSaveBroadcast = async () => {
    if (!editingBroadcast) return;
    if (!editDraft.message.trim()) {
      toast.error("Broadcast message cannot be empty.");
      return;
    }

    setEditSubmitting(true);
    try {
      const response = await API.put(`/admin/broadcasts/${editingBroadcast.id}`, {
        message: editDraft.message.trim(),
        type: editDraft.type,
      });
      toast.success(response.data?.msg || "Broadcast updated successfully.");
      setEditingBroadcast(null);
      setEditDraft({ message: "", type: "info" });
      await loadBroadcasts({ limit: historyLimit, offset: historyOffset });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update broadcast."));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteBroadcast = async () => {
    if (!deleteTarget) return;

    setDeleteSubmitting(true);
    try {
      const response = await API.delete(`/admin/broadcasts/${deleteTarget.id}`);
      toast.success(response.data?.msg || "Broadcast deleted successfully.");

      const nextOffset =
        historyOffset > 0 && broadcasts.length === 1
          ? Math.max(0, historyOffset - historyLimit)
          : historyOffset;

      setDeleteTarget(null);
      await loadBroadcasts({ limit: historyLimit, offset: nextOffset });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete broadcast."));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const latestBroadcast = broadcasts[0] || null;
  const filteredBroadcasts = broadcasts.filter((broadcast) => {
    const matchesCategory = historyCategory === "all" || broadcast.type === historyCategory;
    const searchTerm = historySearch.trim().toLowerCase();
    const matchesSearch =
      !searchTerm ||
      [
        broadcast.message,
        broadcast.type,
        broadcast.admin?.email,
        broadcast.createdAt,
        broadcast.updatedAt,
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(searchTerm));

    return matchesCategory && matchesSearch;
  });
  const filteredCount = filteredBroadcasts.length;
  const showingFrom = historyTotal === 0 ? 0 : historyOffset + 1;
  const showingTo =
    historyTotal === 0 ? 0 : Math.min(historyOffset + broadcasts.length, historyTotal);
  const canGoPrev = historyOffset > 0;
  const canGoNext = historyOffset + historyLimit < historyTotal;
  const telegramRecipientCount = Number(telegramStatus?.userBroadcast?.recipientCount || 0);
  const telegramConfigured = Boolean(
    telegramStatus?.userBroadcast?.configured ?? telegramStatus?.hasBotToken,
  );
  const telegramSendUnavailable =
    deliveryMode === "telegram" &&
    telegramStatus &&
    (!telegramConfigured || telegramRecipientCount < 1);

  return (
    <div className="admin-page admin-broadcast-page">
      <div className="admin-broadcast-header">
        <div>
          <h1>
            <Megaphone className="admin-broadcast-header-icon" />
            <span>Broadcast Hub</span>
          </h1>
          <p>Manage site broadcasts and Telegram delivery from one focused operations page.</p>
        </div>
      </div>

      <div className="admin-broadcast-summary-grid">
        <div className="admin-broadcast-summary-card">
          <div className="admin-broadcast-summary-icon">
            {deliveryMode === "telegram" ? <RadioTower size={20} /> : <Megaphone size={20} />}
          </div>
          <div className="admin-broadcast-summary-copy">
            <span>Send Mode</span>
            <strong>{deliveryMode === "telegram" ? "Telegram" : "Broadcast"}</strong>
            <small>Current delivery mode</small>
          </div>
        </div>
        <div className="admin-broadcast-summary-card">
          <div className="admin-broadcast-summary-icon">
            <MessageSquareText size={20} />
          </div>
          <div className="admin-broadcast-summary-copy">
            <span>Audience</span>
            <strong>{deliveryMode === "telegram" ? "Connected Chats" : "All Users"}</strong>
            <small>
              {deliveryMode === "telegram"
                ? telegramConfigured
                  ? `${telegramRecipientCount} connected Telegram ${telegramRecipientCount === 1 ? "chat" : "chats"}`
                  : "Telegram bot token missing"
                : "Broadcast delivery target"}
            </small>
          </div>
        </div>
        <div className="admin-broadcast-summary-card">
          <div className="admin-broadcast-summary-icon">
            <Settings2 size={20} />
          </div>
          <div className="admin-broadcast-summary-copy">
            <span>Saved Broadcasts</span>
            <strong>{historyTotal}</strong>
            <small>
              {latestBroadcast
                ? `Latest update ${formatTimestamp(latestBroadcast.createdAt)}`
                : "No saved broadcasts yet"}
            </small>
          </div>
        </div>
        <div className="admin-broadcast-summary-card">
          <div className="admin-broadcast-summary-icon">
            <Search size={20} />
          </div>
          <div className="admin-broadcast-summary-copy">
            <span>Connected Chats</span>
            <strong>{telegramRecipientCount}</strong>
            <small>
              {telegramConfigured ? `Telegram broadcast is ready` : "Telegram bot token missing"}
            </small>
          </div>
        </div>
      </div>

      <section className="admin-card admin-broadcast-card">
        <div className="admin-card-heading">
          <div className="admin-broadcast-card-copy">
            <div className="admin-card-title">
              <Megaphone />
              <span>Broadcast Center</span>
            </div>
            <p>Send platform-wide notices without leaving the admin workspace.</p>
          </div>
          <span className="admin-badge admin-badge-info">
            <Megaphone size={12} />
            Live
          </span>
        </div>

        <form className="admin-broadcast-composer" onSubmit={handleBroadcastSubmit}>
          <div className="admin-field-group admin-broadcast-message-wrap">
            <label className="admin-label">Message</label>
            <textarea
              className="admin-textarea admin-broadcast-message"
              rows={4}
              value={broadcastMessage}
              onChange={(event) => setBroadcastMessage(event.target.value)}
              placeholder="Write a system-wide update"
            />
          </div>

          <div className="admin-broadcast-side">
            <div className="admin-field-group">
              <label className="admin-label">Mode</label>
              <div className="admin-broadcast-mode-group" role="tablist" aria-label="Delivery mode">
                <button
                  type="button"
                  className={`admin-broadcast-mode-btn${deliveryMode === "broadcast" ? " active" : ""}`}
                  onClick={() => setDeliveryMode("broadcast")}
                >
                  Broadcast
                </button>
                <button
                  type="button"
                  className={`admin-broadcast-mode-btn${deliveryMode === "telegram" ? " active" : ""}`}
                  onClick={() => setDeliveryMode("telegram")}
                >
                  Telegram
                </button>
              </div>
            </div>

            <div className="admin-field-group">
              <label className="admin-label">
                {deliveryMode === "telegram" ? "Audience" : "Type"}
              </label>
              {deliveryMode === "telegram" ? (
                <div className="admin-broadcast-static-target">
                  <RadioTower size={15} />
                  <span>
                    {telegramConfigured
                      ? `${telegramRecipientCount} connected ${telegramRecipientCount === 1 ? "chat" : "chats"}`
                      : "Bot not configured"}
                  </span>
                </div>
              ) : (
                <CustomSelect
                  value={broadcastType}
                  onChange={setBroadcastType}
                  options={BROADCAST_TYPE_OPTIONS}
                />
              )}
            </div>

            <div className="admin-broadcast-actions">
              <div
                className={`admin-broadcast-action-row${deliveryMode === "telegram" ? " single" : ""}`}
              >
                {deliveryMode === "broadcast" && (
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost admin-broadcast-preview-trigger"
                    onClick={handlePreviewBroadcast}
                    disabled={actionBusy === "broadcast" || !broadcastMessage.trim()}
                  >
                    <MessageSquareText size={15} />
                    Preview Broadcast
                  </button>
                )}
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-broadcast-send-btn"
                  disabled={
                    actionBusy === "broadcast" ||
                    actionBusy === "telegram" ||
                    telegramSendUnavailable
                  }
                >
                  <Send size={15} />
                  {actionBusy === "telegram"
                    ? "Sending..."
                    : actionBusy === "broadcast"
                      ? "Sending..."
                      : deliveryMode === "telegram"
                        ? "Send to Telegram"
                        : "Send Broadcast"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </section>

      <section className="admin-card admin-broadcast-card">
        <div className="admin-card-heading">
          <div className="admin-broadcast-card-copy">
            <div className="admin-card-title">
              <MessageSquareText />
              <span>Broadcast Archive</span>
            </div>
            <p>Review, revise, and remove saved broadcasts without leaving the admin workspace.</p>
          </div>
        </div>

        <div className="admin-broadcast-toolbar">
          <div className="admin-field-group admin-broadcast-toolbar-search">
            <label className="admin-label">
              <Search size={13} />
              Search broadcasts
            </label>
            <input
              type="text"
              className="admin-input"
              placeholder="Search broadcasts"
              value={historySearch}
              onChange={(event) => setHistorySearch(event.target.value)}
            />
          </div>
          <div className="admin-field-group admin-broadcast-category-filter">
            <label className="admin-label">Category</label>
            <CustomSelect
              value={historyCategory}
              onChange={setHistoryCategory}
              options={BROADCAST_FILTER_OPTIONS}
            />
          </div>
          <div className="admin-field-group admin-broadcast-page-size">
            <label className="admin-label">Count</label>
            <CustomSelect
              value={String(historyLimit)}
              onChange={(value) => loadBroadcasts({ limit: Number(value), offset: 0 })}
              options={PAGE_SIZE_OPTIONS}
            />
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-ghost admin-broadcast-refresh-btn"
            onClick={() => loadBroadcasts({ limit: historyLimit, offset: historyOffset })}
            disabled={historyLoading || historyRefreshing}
          >
            <RefreshCcw size={15} />
            {historyRefreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {historyError && <div className="admin-inline-alert error">{historyError}</div>}

        {historyLoading ? (
          <div className="admin-broadcast-history-list">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="admin-broadcast-entry admin-broadcast-entry-skeleton">
                <div className="admin-broadcast-entry-head">
                  <div className="admin-broadcast-entry-badges">
                    <span className="admin-skeleton-line admin-skeleton-line-sm" />
                    <span className="admin-skeleton-line admin-skeleton-line-xs" />
                  </div>
                  <div className="admin-broadcast-entry-actions">
                    <span className="admin-skeleton-line admin-skeleton-line-btn" />
                    <span className="admin-skeleton-line admin-skeleton-line-btn" />
                  </div>
                </div>
                <span className="admin-skeleton-line admin-skeleton-line-lg" />
                <span className="admin-skeleton-line admin-skeleton-line-lg" />
                <div className="admin-broadcast-entry-meta">
                  <span className="admin-skeleton-line admin-skeleton-line-md" />
                  <span className="admin-skeleton-line admin-skeleton-line-md" />
                </div>
              </div>
            ))}
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="admin-empty">No broadcasts saved yet.</div>
        ) : filteredBroadcasts.length === 0 ? (
          <div className="admin-empty">
            No broadcasts found for the selected category on this page.
          </div>
        ) : (
          <div className="admin-broadcast-history-list">
            {filteredBroadcasts.map((broadcast, index) => {
              const typeMeta = getBroadcastTypeMeta(broadcast.type);
              const createdAt = formatTimestamp(broadcast.createdAt);
              const updatedAt = formatTimestamp(broadcast.updatedAt);
              const wasEdited = Boolean(
                broadcast.updatedAt && broadcast.updatedAt !== broadcast.createdAt,
              );

              return (
                <article
                  key={broadcast.id}
                  className={`admin-broadcast-entry admin-broadcast-entry-${broadcast.type || "info"}`}
                >
                  <div className="admin-broadcast-entry-head">
                    <div className="admin-broadcast-entry-badges">
                      <span className={`admin-badge ${typeMeta.badgeClass}`}>{typeMeta.label}</span>
                      {historyCategory === "all" && historyOffset === 0 && index === 0 && (
                        <span className="admin-badge admin-badge-neutral">Latest</span>
                      )}
                    </div>

                    <div className="admin-broadcast-entry-actions">
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-broadcast-entry-btn"
                        onClick={() => openEditModal(broadcast)}
                      >
                        <PencilLine size={15} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger admin-broadcast-entry-btn"
                        onClick={() => setDeleteTarget(broadcast)}
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                    </div>
                  </div>

                  <p className="admin-broadcast-entry-message">{broadcast.message}</p>

                  <div className="admin-broadcast-entry-meta">
                    <span>Created {createdAt}</span>
                    <span>{wasEdited ? `Updated ${updatedAt}` : "Not edited"}</span>
                    <span>By {broadcast.admin?.email || "Unknown admin"}</span>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="admin-table-footer admin-broadcast-history-footer">
          <div className="admin-table-summary">
            {historyCategory === "all"
              ? `Showing ${showingFrom}-${showingTo} of ${historyTotal}`
              : `Showing ${filteredCount} ${filteredCount === 1 ? "broadcast" : "broadcasts"} in ${getBroadcastTypeMeta(historyCategory).label}`}
          </div>
          <div className="admin-table-pagination">
            <button
              type="button"
              className="admin-btn admin-btn-ghost"
              onClick={() =>
                loadBroadcasts({
                  limit: historyLimit,
                  offset: Math.max(0, historyOffset - historyLimit),
                })
              }
              disabled={!canGoPrev || historyLoading || historyRefreshing}
            >
              Previous
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-ghost"
              onClick={() =>
                loadBroadcasts({ limit: historyLimit, offset: historyOffset + historyLimit })
              }
              disabled={!canGoNext || historyLoading || historyRefreshing}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <PageModal
        open={Boolean(editingBroadcast)}
        onClose={closeEditModal}
        onConfirm={handleSaveBroadcast}
        title="Edit broadcast"
        description="Update the saved broadcast text or severity type, then save your changes."
        confirmLabel={editSubmitting ? "Saving..." : "Save changes"}
        loading={editSubmitting}
      >
        <div className="admin-broadcast-modal-fields">
          <div className="admin-field-group">
            <label className="admin-label">Message</label>
            <textarea
              className="admin-textarea admin-broadcast-modal-textarea"
              value={editDraft.message}
              onChange={(event) =>
                setEditDraft((current) => ({ ...current, message: event.target.value }))
              }
              placeholder="Update broadcast text"
            />
          </div>
          <div className="admin-field-group">
            <label className="admin-label">Type</label>
            <CustomSelect
              value={editDraft.type}
              onChange={(value) => setEditDraft((current) => ({ ...current, type: value }))}
              options={BROADCAST_TYPE_OPTIONS}
            />
          </div>
        </div>
      </PageModal>

      <PageModal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleDeleteBroadcast}
        title="Delete this broadcast?"
        description={`This will permanently remove the ${getBroadcastTypeMeta(deleteTarget?.type).label.toLowerCase()} broadcast from saved history.`}
        confirmLabel={deleteSubmitting ? "Deleting..." : "Delete broadcast"}
        loading={deleteSubmitting}
        tone="danger"
      />
    </div>
  );
}
